/**
 * @file Submission UI — backgroundStageFieldOrder.
 * Single source of truth for Background stage field merge, sort, dedupe,
 * lifecycle block order, and raw-material section split.
 * Used by contributor BackgroundData form and SubmissionStagesFormReadonly (Dataset Overview).
 */

import type { FormField } from "../../interfaces/formInterfaces";

/** Type definition — Stage Fields Source. */
export type StageFieldsSource = {
  fields?: FormField[] | null;
  ungroupedFields?: FormField[] | null;
  formSubSections?: Array<{ fields?: FormField[] | null }> | null;
  subSections?: Array<{ fields?: FormField[] | null }> | null;
};

/** Exported function — merge Stage Form Fields. */
export function mergeStageFormFields(stage: StageFieldsSource): FormField[] {
  const direct: FormField[] = [
    ...(Array.isArray(stage.fields) ? stage.fields : []),
    ...(Array.isArray(stage.ungroupedFields) ? stage.ungroupedFields : []),
  ];
  const subSections = Array.isArray(stage.formSubSections)
    ? stage.formSubSections
    : Array.isArray(stage.subSections)
      ? stage.subSections
      : [];
  const fromSubs = subSections.flatMap((s) =>
    Array.isArray(s.fields) ? s.fields : []
  );
  return [...direct, ...fromSubs];
}

/** Exported function — sort Fields By Order. */
export function sortFieldsByOrder<T extends { order?: number }>(fields: T[]): T[] {
  return [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Sort by `order`, then keep first occurrence of each `name` (matches duplicate keys across sources). */
export function dedupeFormFieldsByNameSorted(fields: FormField[]): FormField[] {
  const sorted = sortFieldsByOrder(fields);
  const seen = new Set<string>();
  return sorted.filter((f) => {
    if (!f?.name || seen.has(f.name)) return false;
    seen.add(f.name);
    return true;
  });
}

/** Exported function — get Stage Ordered Fields. */
export function getStageOrderedFields(
  stage: StageFieldsSource | null | undefined
): FormField[] {
  if (!stage) return [];
  return dedupeFormFieldsByNameSorted(mergeStageFormFields(stage));
}

/** Background contributor UI groups these when it hits checkbox `a1`. */
export const BACKGROUND_LIFECYCLE_BLOCK_ORDER = [
  "dataCompleteness",
  "a1",
  "a2",
  "a3",
  "a1_a3",
  "timelineAvailable",
] as const;

/** Exported function — apply Background Lifecycle Display Order. */
export function applyBackgroundLifecycleDisplayOrder(
  fields: FormField[]
): FormField[] {
  const sorted = sortFieldsByOrder(fields);
  const blockSet = new Set<string>(
    BACKGROUND_LIFECYCLE_BLOCK_ORDER as unknown as string[]
  );
  const byName = new Map(
    sorted.filter((f) => f.name).map((f) => [String(f.name), f])
  );
  const present = BACKGROUND_LIFECYCLE_BLOCK_ORDER.map((n) => byName.get(n)).filter(
    (f): f is FormField => Boolean(f)
  );
  if (!present.length || !byName.get("a1")) return sorted;

  const out: FormField[] = [];
  let inserted = false;
  for (const f of sorted) {
    if (!f.name) continue;
    if (f.name === "a1" && !inserted) {
      for (const b of present) out.push(b);
      inserted = true;
      continue;
    }
    if (blockSet.has(f.name)) continue;
    out.push(f);
  }
  return out;
}

/** Main value field → unit field(s), form + API keys (same as `useBackgroundData` `pairedGroups`). */
export const BACKGROUND_VALUE_UNIT_FIELDS: Record<string, readonly string[]> = {
  density: ["density_unit"],
  uValue: ["uValue_unit"],
  compressiveStrength: ["compressiveStrength_unit"],
  tensileStrength: ["tensileStrength_unit"],
  temperature: ["temperature_unit"],
  diameter: ["diameterUnit", "diameterUnitId"],
  coverage: ["coverageUnit", "coverageUnitId"],
  thickness: ["thicknessUnit", "thicknessUnitId"],
};

/** @deprecated Use BACKGROUND_VALUE_UNIT_FIELDS — first unit per main (form field name). */
export const BACKGROUND_VALUE_UNIT_PAIRS: Record<string, string> = Object.fromEntries(
  Object.entries(BACKGROUND_VALUE_UNIT_FIELDS).map(([main, units]) => [main, units[0]])
);

/** Unit-side keys for paired numeric + unit rows. */
export const BACKGROUND_PAIRED_UNIT_FIELD_NAMES = new Set(
  Object.values(BACKGROUND_VALUE_UNIT_FIELDS).flat()
);

/** CMS form unit field → API column on `submission.background`. */
const BACKGROUND_UNIT_FORM_TO_API_KEYS: Record<string, string[]> = {
  density_unit: ["densityUnitId"],
  uValue_unit: ["uValueUnitId"],
  compressiveStrength_unit: ["compressiveStrengthUnit"],
  tensileStrength_unit: ["tensileStrengthUnit"],
  temperature_unit: ["temperatureUnitId"],
  diameterUnit: ["diameterUnitId"],
  coverageUnit: ["coverageUnitId"],
  thicknessUnit: ["thicknessUnitId"],
  diameterUnitId: ["diameterUnitId"],
  coverageUnitId: ["coverageUnitId"],
  thicknessUnitId: ["thicknessUnitId"],
};

function backgroundValueForKey(obj: Record<string, unknown>, key: string): unknown {
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const camel = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  if (Object.prototype.hasOwnProperty.call(obj, camel)) return obj[camel];
  return undefined;
}

/** Resolve background read-only values (form field names vs Prisma `*UnitId` columns). */
export function readBackgroundValueForFormField(
  obj: Record<string, unknown> | null | undefined,
  fieldName: string
): unknown {
  if (!obj) return undefined;
  const direct = backgroundValueForKey(obj, fieldName);
  if (direct !== undefined && direct !== null && direct !== "") return direct;
  const apiKeys = BACKGROUND_UNIT_FORM_TO_API_KEYS[fieldName];
  if (apiKeys) {
    for (const key of apiKeys) {
      const v = backgroundValueForKey(obj, key);
      if (v !== undefined && v !== null && v !== "") return v;
    }
  }
  return undefined;
}

/**
 * Place unit fields immediately after their value field (fixes CMS `order` putting
 * Coverage Unit / Diameter Unit at the top of Dataset Overview).
 */
export function applyBackgroundPairedUnitFieldOrder<T extends { name?: string }>(
  fields: T[]
): T[] {
  const unitNames = BACKGROUND_PAIRED_UNIT_FIELD_NAMES;
  const byName = new Map(
    fields.filter((f) => f.name).map((f) => [String(f.name), f])
  );
  const placed = new Set<string>();
  const out: T[] = [];

  for (const f of fields) {
    const name = f.name ? String(f.name) : "";
    if (!name || placed.has(name)) continue;
    if (unitNames.has(name)) continue;

    out.push(f);
    placed.add(name);

    const unitKeys = BACKGROUND_VALUE_UNIT_FIELDS[name];
    if (unitKeys) {
      for (const unitKey of unitKeys) {
        const unitField = byName.get(unitKey);
        if (unitField && !placed.has(unitKey)) {
          out.push(unitField);
          placed.add(unitKey);
        }
      }
    }
  }

  for (const f of fields) {
    const name = f.name ? String(f.name) : "";
    if (name && !placed.has(name)) {
      out.push(f);
      placed.add(name);
    }
  }

  return out;
}

/** Exported function — get Background Stage Display Fields. */
export function getBackgroundStageDisplayFields(
  stage: StageFieldsSource | null | undefined
): FormField[] {
  const ordered = applyBackgroundLifecycleDisplayOrder(getStageOrderedFields(stage));
  return applyBackgroundPairedUnitFieldOrder(ordered).filter(
    (f) => f.name && !isBackgroundDataReviewerSchemaFieldName(f.name)
  );
}

/** CMS-only reviewer anchors (labels/info); not part of contributor field order. */
export function getBackgroundDataReviewerTemplateFields(
  stage: StageFieldsSource | null | undefined
): { nameField: FormField | null; emailField: FormField | null } {
  const all = getStageOrderedFields(stage);
  return {
    nameField: all.find((f) => f.name === "dataReviewerName_01") ?? null,
    emailField: all.find((f) => f.name === "dataReviewerEmail_01") ?? null,
  };
}

/** Exported function — get Background Data Type Field. */
export function getBackgroundDataTypeField(
  stage: StageFieldsSource | null | undefined
): FormField | null {
  return getStageOrderedFields(stage).find((f) => f.name === "dataType") ?? null;
}

/** Omit paired unit fields from read-only lists; values still resolve on the main field row via `resolveDisplayValue`. */
export function omitBackgroundPairedUnitDisplayFields<T extends { name?: string }>(
  fields: T[]
): T[] {
  return fields.filter((f) => f.name && !BACKGROUND_PAIRED_UNIT_FIELD_NAMES.has(f.name));
}

/** API-only unit columns — hide from Dataset Overview extras when form unit field exists. */
export const BACKGROUND_API_UNIT_OBJECT_KEYS = new Set([
  "densityUnitId",
  "uValueUnitId",
  "compressiveStrengthUnit",
  "tensileStrengthUnit",
  "temperatureUnitId",
  "diameterUnitId",
  "coverageUnitId",
  "thicknessUnitId",
]);

/** Exported function — is Background Api Only Unit Object Key. */
export function isBackgroundApiOnlyUnitObjectKey(key: string): boolean {
  return BACKGROUND_API_UNIT_OBJECT_KEYS.has(key);
}

/** Exported function — is Background Raw Material Schema Field Name. */
export function isBackgroundRawMaterialSchemaFieldName(name: string): boolean {
  if (/^rawMaterial_\d+$/i.test(name)) return true;
  if (/^rawMaterialUnit_\d+$/i.test(name)) return true;
  /* Form + CMS use `rawMaterial_01_qty` etc.; table rows use `qty` only — hide from scalar grid. */
  if (/^rawMaterial_\d+_qty$/i.test(name)) return true;
  return false;
}

/** Exported function — is Background Data Reviewer Schema Field Name. */
export function isBackgroundDataReviewerSchemaFieldName(name: string): boolean {
  if (name === "dataReviewerName" || name === "dataReviewerEmail") {
    return true;
  }
  return /^dataReviewer(Name|Email)_\d{2}$/i.test(name);
}

/**
 * Scalar fields before / after the raw-material block anchor (`rawMaterial_01`),
 * excluding raw material repeat keys (shown only in the raw-materials UI).
 * Uses position in `sortedBg` (after paired-unit reorder), not CMS `order`, so unit
 * fields stay next to Coverage/Diameter instead of jumping to the top section.
 */
export function partitionBackgroundFieldsAroundRawMaterials(
  sortedBg: FormField[]
): { before: FormField[]; after: FormField[] } {
  const anchor =
    sortedBg.find((f) => f.name === "rawMaterial_01") ??
    sortedBg.find((f) => f.name && /^rawMaterial_\d+$/i.test(f.name));

  const scalars = sortedBg.filter(
    (f) =>
      f.name &&
      !isBackgroundRawMaterialSchemaFieldName(f.name) &&
      !isBackgroundDataReviewerSchemaFieldName(f.name)
  );

  if (!anchor?.name) {
    return { before: applyBackgroundPairedUnitFieldOrder(scalars), after: [] };
  }

  const anchorIdx = sortedBg.findIndex((f) => f.name === anchor.name);
  const before: FormField[] = [];
  const after: FormField[] = [];

  for (const f of scalars) {
    const idx = sortedBg.findIndex((x) => x.name === f.name);
    if (idx !== -1 && idx < anchorIdx) before.push(f);
    else after.push(f);
  }

  return {
    before: applyBackgroundPairedUnitFieldOrder(before),
    after: applyBackgroundPairedUnitFieldOrder(after),
  };
}
